import {
  HeaderBreadcrumbs,
  LoadingContainer,
} from '@pflegenavi/web-components';
import IconBellFill from '~icons/eva/bell-fill';
import IconFileFill from '~icons/eva/file-fill';
import IconClockFading from '~icons/ph/clock-countdown-bold';
import IconScaleFill from '~icons/ph/scales-fill';

import {
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import type {
  GetDashboardResult,
  ReceiptPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  useGetDashboardPhoenix,
  useReceiptsPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { useLocale } from '@pflegenavi/frontend/localization';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import {
  FeatureFlag,
  FeatureFlagStatus,
  ReceiptStatusValues,
} from '@pflegenavi/shared/api';
import { YEAR_MONTH_FORMAT } from '@pflegenavi/shared/constants';
import type { FC, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { BalanceCard } from './components/BalanceCard';
import { BudgetsCard } from './components/BudgetsCard';
import { CardPaymentsCard } from './components/CardPaymentsCard';
import { DashboardStatBar } from './components/DashboardStatBar';
import { ReceiptAnalytic } from './components/ReceiptAnalytic';
import { ReceiptTypeChart } from './components/ReceiptTypeChart';
import { RecentReceiptsTable } from './components/RecentReceipts';

import {
  useNursingHomeContext,
  useScope,
} from '@pflegenavi/frontend/nursing-home-context';
import { TabFilterValues } from '@pflegenavi/frontend/pages/residents';
import NoLowBalanceResidentsSvg from './assets/undraw_no_records.svg';
import { useSelectedEmployeeIds } from '@pflegenavi/frontend/nursing-home-context';
import {
  useFeatureFlag,
  useFeatureFlagEnabled,
} from '@pflegenavi/frontend/feature-flags';

export function DashboardPage(): JSX.Element {
  const { kind } = useScope();

  // Every widget below is nursing-home-bound, so rendering them at a wider
  // scope would show the last selected home's numbers under a header that
  // claims otherwise.
  if (kind !== 'nursing_home') {
    return <ScopeUnsupportedDashboardView />;
  }

  return <NursingHomeDashboard />;
}

const ScopeUnsupportedDashboardView: FC = () => {
  const { t } = useTranslation();

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[{ name: t('dashboard.main'), href: nhAppMainPages.DASHBOARD }]}
      />
      <Card sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {t('dashboard.scope-unsupported.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.scope-unsupported.description')}
        </Typography>
      </Card>
    </Container>
  );
};

const NursingHomeDashboard: FC = () => {
  const { selectedNursingHome } = useNursingHomeContext();

  const receiptManagementEnabled =
    useFeatureFlag(FeatureFlag.ReceiptManagement) === FeatureFlagStatus.Enabled;

  const { data: analytics, isLoading } = useGetDashboardPhoenix({
    nursingHomeId: selectedNursingHome?.id,
    month: new Date(),
  });

  const employeeIds = useSelectedEmployeeIds();

  const { data: receiptsToEdit } = useReceiptsPhoenix({
    nursing_home_ids: selectedNursingHome
      ? [selectedNursingHome.id]
      : undefined,
    page: 1,
    page_size: 5,
    receipt_states: [ReceiptStatusValues.DRAFT, ReceiptStatusValues.INCOMPLETE],
    created_by_employee_ids: employeeIds,
  });

  if (!receiptManagementEnabled) {
    return <EmptyDashboardView />;
  }

  return (
    <DashboardPageView
      isLoading={isLoading}
      oldAnalytics={analytics}
      analytics={{
        draftCount: receiptsToEdit?.status_totals?.Draft ?? 0,
        incompleteCount: receiptsToEdit?.status_totals?.Incomplete ?? 0,
        awaitingSettlementCount:
          receiptsToEdit?.status_totals?.AwaitingSettlement ?? 0,
        pendingCount: receiptsToEdit?.status_totals?.Pending ?? 0,
        totalCount:
          (receiptsToEdit?.status_totals?.Draft ?? 0) +
          (receiptsToEdit?.status_totals?.Incomplete ?? 0) +
          (receiptsToEdit?.status_totals?.AwaitingSettlement ?? 0) +
          (receiptsToEdit?.status_totals?.Pending ?? 0),
      }}
      receiptsToEdit={receiptsToEdit?.data}
    />
  );
};

const EmptyDashboardView: FC = () => {
  const locale = useLocale();
  const { t } = useTranslation();

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          {
            name: t('dashboard.main'),
            href: nhAppMainPages.DASHBOARD,
          },
          { name: new Date().toLocaleDateString(locale, YEAR_MONTH_FORMAT) },
        ]}
      />
      <Stack direction="column" gap={3}>
        <DashboardStatBar />
        <Stack direction="row" flexWrap="wrap" gap={3} alignItems="flex-start">
          <BudgetsCard />
          <CardPaymentsCard />
        </Stack>
      </Stack>
    </Container>
  );
};

interface DashBoardPageViewProps {
  isLoading: boolean;
  analytics:
    | {
        draftCount: number;
        incompleteCount: number;
        awaitingSettlementCount: number;
        pendingCount: number;
        totalCount: number;
      }
    | undefined;
  receiptsToEdit: ReceiptPhoenix[] | undefined;
  oldAnalytics: GetDashboardResult | undefined;
}

export const DashboardPageView: FC<DashBoardPageViewProps> = ({
  isLoading,
  analytics,
  oldAnalytics,
  receiptsToEdit,
}) => {
  const theme = useTheme();
  const locale = useLocale();
  const { t } = useTranslation();
  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  if (!analytics || !oldAnalytics || isLoading) {
    return <LoadingContainer height={200} />;
  }

  const lowBalanceCard = (
    <BalanceCard
      negative={false}
      cardTitle={t('dashboard.label.belowThreshold')}
      dataCyPrefix="balance-below-threshold"
      data={oldAnalytics?.data?.balance_below_threshold.residents}
      navigateTo={TabFilterValues.LOW_BALANCE}
      noDataImg={NoLowBalanceResidentsSvg}
      noDataText={t('table.no-low-balance')}
    />
  );

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('dashboard.main')}
        links={[
          {
            name: t('dashboard.main'),
            href: nhAppMainPages.DASHBOARD,
          },
          { name: new Date().toLocaleDateString(locale, YEAR_MONTH_FORMAT) },
        ]}
      />
      <Stack direction="column" gap={3}>
        <Card data-cy="receipts-breakdown">
          <AnalyticsGridContainer container>
            <Grid
              size={{
                xs: 6,
                lg: 3,
              }}
            >
              <ReceiptAnalytic
                data-cy="receipts-incomplete"
                title={t('analytics.label.incomplete')}
                total={analytics.incompleteCount}
                percent={
                  (100 * analytics.incompleteCount) / analytics.totalCount
                }
                color={theme.palette.error.main}
                Icon={() => (
                  <IconBellFill color={theme.palette.error.main}></IconBellFill>
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 6,
                lg: 3,
              }}
            >
              <ReceiptAnalytic
                data-cy="receipts-draft"
                title={t('analytics.label.draft')}
                total={analytics.draftCount}
                percent={(100 * analytics.draftCount) / analytics.totalCount}
                color={theme.palette.text.secondary}
                Icon={() => (
                  <IconFileFill color={theme.palette.text.secondary} />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 6,
                lg: 3,
              }}
            >
              <ReceiptAnalytic
                data-cy="receipts-pending"
                title={t('analytics.label.pending')}
                total={analytics.pendingCount}
                percent={(100 * analytics.pendingCount) / analytics.totalCount}
                color={theme.palette.warning.main}
                Icon={() => (
                  <IconClockFading color={theme.palette.warning.main} />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 6,
                lg: 3,
              }}
            >
              <ReceiptAnalytic
                data-cy="receipts-awaiting-settlement"
                title={t('analytics.label.awaitingSettlement')}
                total={analytics.awaitingSettlementCount}
                percent={
                  (100 * analytics.awaitingSettlementCount) /
                  analytics.totalCount
                }
                color={theme.palette.info.main}
                Icon={() => <IconScaleFill color={theme.palette.info.main} />}
              />
            </Grid>
          </AnalyticsGridContainer>
        </Card>
        <Stack direction="row" flexWrap="wrap" gap={3}>
          <RecentReceiptsTable data={receiptsToEdit ?? []} />
          <BalanceCard
            negative={true}
            cardTitle={t('dashboard.label.negativeBalance')}
            dataCyPrefix="balance-negative"
            data={oldAnalytics?.data?.negative_balance.residents}
            navigateTo={TabFilterValues.NEGATIVE_BALANCE}
            noDataImg={NoLowBalanceResidentsSvg}
            noDataText={t('table.no-negative-balance')}
          />
        </Stack>
        {expenseManagementEnabled ? (
          // Receipt + expense management on: drop the Categories chart and put
          // budgets next to the low-balance residents.
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={3}
            alignItems="flex-start"
          >
            <BudgetsCard />
            {lowBalanceCard}
          </Stack>
        ) : (
          <>
            <BudgetsCard />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ReceiptTypeChart data={oldAnalytics?.data?.receipt_types} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>{lowBalanceCard}</Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Container>
  );
};

const AnalyticsGridContainer = styled(Grid)(({ theme }) =>
  theme.unstable_sx({
    padding: 2,
    '& > .MuiGrid-root': {
      borderRight: '1px dashed lightGrey',
      borderBottom: {
        xs: '1px dashed lightGrey',
        lg: '0px',
      },
    },
    '& > .MuiGrid-root:nth-of-type(even)': {
      borderRightWidth: {
        xs: 0,
        lg: 1,
      },
    },
    '& > .MuiGrid-root:nth-last-of-type(-n + 2)': {
      borderBottomWidth: 0,
    },
    '& > .MuiGrid-root:last-of-type': {
      borderRightWidth: 0,
    },
  })
);
